/**
 * GLOBAL SETTINGS PROVIDER
 * ========================
 * Provider unificado para sincronizar tema, idioma, e moeda
 * com persistência no localStorage e sincronização entre abas
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Theme = 'light' | 'dark' | 'system';
type Language = 'pt-BR' | 'en-US' | 'es-ES' | 'ja-JP' | 'ru-RU';
type Currency = 'BRL' | 'USD' | 'EUR';

interface GlobalSettings {
  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;

  // Language
  language: Language;
  setLanguage: (language: Language) => void;

  // Currency
  currency: Currency;
  setCurrency: (currency: Currency) => void;

  // Utilities
  formatPrice: (amount: number) => string;
  isLoading: boolean;
}

const GlobalSettingsContext = createContext<GlobalSettings | null>(null);

// Storage keys
const STORAGE_KEYS = {
  THEME: 'chatbot-imoveis-theme',
  LANGUAGE: 'chatbot-imoveis-language',
  CURRENCY: 'chatbot-imoveis-currency',
} as const;

// Default values
const DEFAULTS = {
  THEME: 'light' as Theme,
  LANGUAGE: 'pt-BR' as Language,
  CURRENCY: 'BRL' as Currency,
} as const;

// Currency symbols and formatters
const CURRENCY_CONFIG = {
  BRL: { symbol: 'R$', locale: 'pt-BR' },
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'de-DE' },
} as const;

export function GlobalSettingsProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  // States with localStorage persistence
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEYS.THEME) as Theme) || DEFAULTS.THEME;
    } catch {
      return DEFAULTS.THEME;
    }
  });

  const [language, setLanguageState] = useState<Language>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEYS.LANGUAGE) as Language) || DEFAULTS.LANGUAGE;
    } catch {
      return DEFAULTS.LANGUAGE;
    }
  });

  const [currency, setCurrencyState] = useState<Currency>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEYS.CURRENCY) as Currency) || DEFAULTS.CURRENCY;
    } catch {
      return DEFAULTS.CURRENCY;
    }
  });

  // Theme handler with DOM application
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);

    try {
      localStorage.setItem(STORAGE_KEYS.THEME, newTheme);

      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');

      let appliedTheme: string;
      if (newTheme === 'system') {
        appliedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else {
        appliedTheme = newTheme;
      }

      root.classList.add(appliedTheme);

      // Broadcast change to other tabs
      window.dispatchEvent(new CustomEvent('theme-changed', { detail: newTheme }));
    } catch (error) {
      console.warn('Failed to persist theme:', error);
    }
  }, []);

  // Language handler with i18n integration
  const setLanguage = useCallback(
    async (newLanguage: Language) => {
      setLanguageState(newLanguage);

      try {
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, newLanguage);

        // Change i18n language
        await i18n.changeLanguage(newLanguage);

        // Auto-update currency based on language
        const languageToCurrency: Record<Language, Currency> = {
          'pt-BR': 'BRL',
          'en-US': 'USD',
          'es-ES': 'EUR',
          'ja-JP': 'USD',
          'ru-RU': 'EUR',
        };

        const suggestedCurrency = languageToCurrency[newLanguage];
        if (suggestedCurrency !== currency) {
          setCurrency(suggestedCurrency);
        }

        // Broadcast change to other tabs
        window.dispatchEvent(new CustomEvent('language-changed', { detail: newLanguage }));
      } catch (error) {
        console.warn('Failed to change language:', error);
      }
    },
    [currency, i18n]
  );

  // Currency handler
  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);

    try {
      localStorage.setItem(STORAGE_KEYS.CURRENCY, newCurrency);

      // Broadcast change to other tabs
      window.dispatchEvent(new CustomEvent('currency-changed', { detail: newCurrency }));
    } catch (error) {
      console.warn('Failed to persist currency:', error);
    }
  }, []);

  // Price formatter
  const formatPrice = useCallback(
    (amount: number): string => {
      const config = CURRENCY_CONFIG[currency];

      try {
        return new Intl.NumberFormat(config.locale, {
          style: 'currency',
          currency: currency,
        }).format(amount);
      } catch {
        return `${config.symbol} ${amount.toLocaleString()}`;
      }
    },
    [currency]
  );

  // Initialize theme on mount
  useEffect(() => {
    setTheme(theme);
    setIsLoading(false);
  }, [setTheme, theme]);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.THEME && e.newValue) {
        setThemeState(e.newValue as Theme);
      } else if (e.key === STORAGE_KEYS.LANGUAGE && e.newValue) {
        setLanguageState(e.newValue as Language);
        i18n.changeLanguage(e.newValue);
      } else if (e.key === STORAGE_KEYS.CURRENCY && e.newValue) {
        setCurrencyState(e.newValue as Currency);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [i18n]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (theme === 'system') {
        setTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [theme, setTheme]);

  const contextValue: GlobalSettings = {
    theme,
    setTheme,
    language,
    setLanguage,
    currency,
    setCurrency,
    formatPrice,
    isLoading,
  };

  return (
    <GlobalSettingsContext.Provider value={contextValue}>{children}</GlobalSettingsContext.Provider>
  );
}

export function useGlobalSettings(): GlobalSettings {
  const context = useContext(GlobalSettingsContext);

  if (!context) {
    throw new Error('useGlobalSettings must be used within a GlobalSettingsProvider');
  }

  return context;
}

// Hook para acessar apenas o tema
export function useTheme() {
  const { theme, setTheme } = useGlobalSettings();
  return { theme, setTheme };
}

// Hook para acessar apenas idioma
export function useLanguage() {
  const { language, setLanguage } = useGlobalSettings();
  return { language, setLanguage };
}

// Hook para acessar apenas moeda
export function useCurrency() {
  const { currency, setCurrency, formatPrice } = useGlobalSettings();
  return { currency, setCurrency, formatPrice };
}
